import {
  ensureProductDataBag,
  getActiveCatalog,
  legacyKeyToRelativePath,
  listLegacyStorageKeysForSlug,
  loadTenantStore,
  saveTenantStore,
  userCatalogToCustomProducts
} from "./chunk-3TOUJ5O5.js";
import {
  brandToTenantRecord
} from "./chunk-WAPZEZZJ.js";
import {
  customProductSlugFromSuffix,
  customSuffixCollidesWithBuiltInProduct
} from "./chunk-OENSNHCR.js";
import {
  EXXAT_STORAGE_PREFIX
} from "./chunk-HA5JJMF5.js";
import {
  runWithoutBuilderDevSync
} from "./chunk-UEG4HCRI.js";
import {
  unregisterProducts
} from "./chunk-6UZ4MMYO.js";

// src/tenant-products/migrate.ts
var RETIRED_TENANT_CATALOG_SLUGS = /* @__PURE__ */ new Set(["exam", "one", "exxam", "product"]);
function isRetiredTenantCatalogSlug(slug) {
  return RETIRED_TENANT_CATALOG_SLUGS.has(slug.trim().toLowerCase());
}
function retireLegacyTenantCatalogProducts(store) {
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const retiredIds = [];
  for (const record of store.catalog) {
    if (record.deletedAt) continue;
    if (!isRetiredTenantCatalogSlug(record.slug)) continue;
    record.deletedAt = now;
    delete store.products[record.id];
    retiredIds.push(record.id);
  }
  if (retiredIds.length > 0) {
    unregisterProducts(retiredIds);
  }
}
function syncCustomProductsIntoCatalog(store, customProducts) {
  const existingBySlug = new Map(
    getActiveCatalog(store).map((p) => [p.slug, p])
  );
  const indexMap = [];
  for (const brand of customProducts) {
    if (!brand?.suffix?.trim() || !brand?.brandColor?.trim()) {
      indexMap.push("");
      continue;
    }
    const slug = customProductSlugFromSuffix(brand.suffix);
    if (customSuffixCollidesWithBuiltInProduct(brand.suffix)) {
      indexMap.push("");
      continue;
    }
    if (isRetiredTenantCatalogSlug(slug)) {
      indexMap.push("");
      continue;
    }
    const existing = existingBySlug.get(slug);
    const dataChanged = !existing || existing.brandSuffix !== brand.suffix.trim() || existing.brandColor !== brand.brandColor;
    const record = existing ? {
      ...existing,
      brandSuffix: brand.suffix.trim(),
      brandColor: brand.brandColor,
      source: "user",
      updatedAt: dataChanged ? (/* @__PURE__ */ new Date()).toISOString() : existing.updatedAt
    } : brandToTenantRecord({ suffix: brand.suffix, brandColor: brand.brandColor });
    if (!existing) {
      store.catalog.push(record);
      existingBySlug.set(slug, record);
    } else {
      const idx = store.catalog.findIndex((p) => p.id === existing.id);
      if (idx >= 0) store.catalog[idx] = record;
    }
    indexMap.push(record.id);
    ensureProductDataBag(store, record.id);
  }
  if (indexMap.some(Boolean)) {
    store.legacyCustomIndexMap = indexMap;
  }
}
function migrateCustomProductsIntoCatalog(store, customProducts) {
  syncCustomProductsIntoCatalog(store, customProducts);
  if (!store.meta.migratedFromCustomProducts) {
    store.meta.migratedFromCustomProducts = true;
    store.meta.lastMigrationAt = (/* @__PURE__ */ new Date()).toISOString();
  }
}
function migrateLegacyStorageKeys(store) {
  if (store.meta.migratedLegacyStorageKeys) return;
  if (typeof window === "undefined") return;
  for (const product of getActiveCatalog(store)) {
    const bag = ensureProductDataBag(store, product.id);
    const keys = listLegacyStorageKeysForSlug(product.slug);
    for (const fullKey of keys) {
      try {
        const raw = window.localStorage.getItem(fullKey);
        if (raw == null) continue;
        const relative = legacyKeyToRelativePath(fullKey);
        if (bag.legacy[relative] === void 0) {
          bag.legacy[relative] = raw;
        }
      } catch {
      }
    }
  }
  store.meta.migratedLegacyStorageKeys = true;
  store.meta.lastMigrationAt = (/* @__PURE__ */ new Date()).toISOString();
}
function removeCustomPrismCatalogCollisions(store) {
  if (store.meta.removedCustomBuiltInSlugCollisions) return;
  const productIds = new Set(
    store.catalog.filter(
      (product) => !product.deletedAt && product.source === "user" && product.slug === "prism"
    ).map((product) => product.id)
  );
  if (productIds.size > 0) {
    store.catalog = store.catalog.filter((product) => !productIds.has(product.id));
    for (const productId of productIds) {
      delete store.products[productId];
    }
    if (store.legacyCustomIndexMap) {
      store.legacyCustomIndexMap = store.legacyCustomIndexMap.filter(
        (id) => !productIds.has(id)
      );
    }
  }
  store.meta.removedCustomBuiltInSlugCollisions = true;
  store.meta.removedCustomPrismProduct = true;
  store.meta.lastMigrationAt = (/* @__PURE__ */ new Date()).toISOString();
}
function purgeUserCreatedTenantProducts(store) {
  if (store.meta.purgedUserCustomProductsV13) return;
  if (typeof window === "undefined") {
    store.meta.purgedUserCustomProductsV13 = true;
    return;
  }
  const userProducts = store.catalog.filter(
    (product) => !product.deletedAt && product.source === "user"
  );
  for (const product of userProducts) {
    deleteTenantProductPermanently(product.id);
  }
  const refreshed = loadTenantStore();
  store.catalog = refreshed.catalog;
  store.products = refreshed.products;
  store.legacyCustomIndexMap = [];
  store.meta.purgedUserCustomProductsV13 = true;
  store.meta.lastMigrationAt = (/* @__PURE__ */ new Date()).toISOString();
}
function runTenantProductMigration(customProducts) {
  const store = loadTenantStore();
  retireLegacyTenantCatalogProducts(store);
  purgeUserCreatedTenantProducts(store);
  migrateCustomProductsIntoCatalog(store, customProducts);
  migrateLegacyStorageKeys(store);
  removeCustomPrismCatalogCollisions(store);
  runWithoutBuilderDevSync(() => saveTenantStore(store));
  const mirror = userCatalogToCustomProducts(store.catalog);
  return { store, customProducts: mirror };
}
function deleteTenantProductPermanently(productId) {
  const store = loadTenantStore();
  const record = store.catalog.find((p) => p.id === productId);
  if (!record) return;
  const slug = record.slug.trim().toLowerCase();
  const deletedSlugs = new Set(store.meta.userDeletedSlugs ?? []);
  if (slug) deletedSlugs.add(slug);
  store.meta.userDeletedSlugs = [...deletedSlugs];
  record.deletedAt = (/* @__PURE__ */ new Date()).toISOString();
  delete store.products[productId];
  if (typeof window !== "undefined") {
    const prefix = `${EXXAT_STORAGE_PREFIX}${record.slug}:`;
    const toRemove = [];
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (key?.startsWith(prefix)) toRemove.push(key);
      }
      for (const key of toRemove) window.localStorage.removeItem(key);
    } catch {
    }
  }
  store.catalog = store.catalog.filter((p) => p.id !== productId);
  store.legacyCustomIndexMap = store.legacyCustomIndexMap?.filter((id) => id !== productId);
  saveTenantStore(store);
}

export {
  RETIRED_TENANT_CATALOG_SLUGS,
  isRetiredTenantCatalogSlug,
  retireLegacyTenantCatalogProducts,
  syncCustomProductsIntoCatalog,
  runTenantProductMigration,
  deleteTenantProductPermanently
};
